import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";
import { tickSequencer } from "../engine/drivers.js";

/**
 * SequencerNode
 * -------------
 * Step sequencer with N slots. Advances one step per beat.
 * Each step holds a value. Outputs the current step's value.
 */
export class SequencerNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Sequencer";

    this.properties = {
      steps: [0, 0.25, 0.5, 0.75, 1, 0.75, 0.5, 0.25],
      bpm: 120,
      stepCount: 8,
    };

    this._currentStep = 0;
    this._accumulator = 0;

    this.addInput("BPM", "number");
    this.addOutput("Value", "number");
    this.addOutput("Step", "number");

    addDrivableSlider(this, "BPM", "bpm", 120, 30, 300, 1);

    finalizeDrivableSliders(this, 1);

    this.size = [200, 100];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const steps = this.properties.steps;
    const count = steps.length;

    const barY = h - 30;
    const barH = 22;
    const pad = 10;
    const stepW = (w - pad * 2) / count;

    for (let i = 0; i < count; i++) {
      const x = pad + i * stepW;
      const val = steps[i];
      const fillH = val * barH;

      // Step background
      ctx.fillStyle = i === this._currentStep
        ? "rgba(78,158,255,0.4)"
        : "rgba(255,255,255,0.08)";
      ctx.fillRect(x + 1, barY, stepW - 2, barH);

      // Value bar
      ctx.fillStyle = i === this._currentStep ? "#4E9EFF" : "rgba(255,255,255,0.35)";
      ctx.fillRect(x + 1, barY + barH - fillH, stepW - 2, fillH);
    }
  }

  onMouseDown(e, localPos) {
    const w = this.size[0];
    const h = this.size[1];
    const steps = this.properties.steps;
    const count = steps.length;
    const barY = h - 30;
    const barH = 22;
    const pad = 10;
    const stepW = (w - pad * 2) / count;

    const x = localPos[0];
    const y = localPos[1];

    if (y >= barY && y <= barY + barH && x >= pad && x <= w - pad) {
      const stepIdx = Math.floor((x - pad) / stepW);
      if (stepIdx >= 0 && stepIdx < count) {
        // Set value based on Y position
        const val = 1 - (y - barY) / barH;
        steps[stepIdx] = Math.max(0, Math.min(1, val));
        return true;
      }
    }
    return false;
  }

  onExecute() {
    syncDrivableSliders(this);

    const bpmInput = this.getInputData(0);
    if (bpmInput != null) this.properties.bpm = bpmInput;

    const { bpm, steps } = this.properties;

    const result = tickSequencer({ step: this._currentStep, accumulator: this._accumulator }, { bpm, steps });
    this._currentStep = result.state.step;
    this._accumulator = result.state.accumulator;

    this.setOutputData(0, result.value);
    this.setOutputData(1, this._currentStep);
    this.setDirtyCanvas(true);
  }
}

LiteGraph.registerNodeType("generators/Sequencer", SequencerNode);
